import React from 'react'
import { CircleAlert } from 'lucide-react'
import { Badge, type BadgeVariant } from '../../components/ui/Badge'
import { ImagePreviewPlaceholder, ImagePreviewThumb } from '../../components/ui/ImagePreviewThumb'
import { cn } from '../../lib/cn'
import { legalStatusTextClass, normalizeLegalStatus } from '../../shared/legalStatus'
import type { CatalogColumn } from './types'
import type { PimMarketplaceCard } from './pim-drafts-api'
import { draftPreviewSources } from './draftPreview'
import { wbDraftStateBadge } from './wbDraftState'

export const STATUS_INFO: Record<string, { label: string; variant: BadgeVariant }> = {
  draft: { label: 'Черновик PIM', variant: 'gray' },
  creating: { label: 'Отправляется', variant: 'yellow' },
  created: { label: 'Создана на МП', variant: 'blue' },
  price_set: { label: 'Цена установлена', variant: 'yellow' },
  stock_set: { label: 'Коммерция только в PIM', variant: 'yellow' },
  selling: { label: 'Историческая отметка PIM', variant: 'gray' },
  error: { label: 'Ошибка', variant: 'red' },
}

/**
 * Пояснение к состоянию «отправлена, ответа нет».
 *
 * Оператор должен понимать, что делать: карточка попадёт в набор кнопки
 * «Выгрузить черновики», и повтор безопасен.
 */
export const UNCONFIRMED_SUBMIT_HINT = 'Запрос ушёл, ответа площадки не было —'
  + ' исход неизвестен. Карточку можно переотправить: она попадает в набор'
  + ' «Выгрузить черновики», а дубля не будет — площадка отбивает второй товар'
  + ' с тем же артикулом.'

/**
 * Пояснение к «отправлена, номера нет» у Яндекс Маркета. Здесь это не молчание
 * площадки: Маркет принял оффер, а номер карточки (`market_sku`) назначает после
 * обработки. Его забирает `ym-publish-confirm-worker` (раз в 5 минут).
 */
export const YM_AWAITING_SKU_HINT = 'Маркет принял оффер и назначит номер карточки'
  + ' после обработки — обычно 10–20 минут. ПИМ проверяет каждые 5 минут: получив'
  + ' номер, карточка уйдёт из черновиков, при отказе Маркета станет «Ошибкой»'
  + ' с его текстом. Переотправлять не нужно.'

/**
 * Состояние карточки для бейджа: `status` плюс явное состояние отправки.
 *
 * Одного `status` мало. «Ошибка» бывает двух совершенно разных природ: отказ
 * площадки (повторяем и разбираемся) и молчание после отправки (повторяем, но
 * причина не в карточке). 09.09.2026 кабинет ВБ КСА собрал 86 карточек второго
 * рода, и по вкладке их было не отличить от первого — оператор искал ошибку в
 * данных, которых в них нет.
 *
 * У Wildberries разбор с 10.09.2026 подробнее и живёт отдельно
 * ([[wbDraftState]]): там «отправлено без ответа» разделено на «ВБ принял, ждём
 * nmID» и «ВБ вернул ошибку», а каждая ошибка называется своими словами.
 * Причина выделения — разные механизмы площадок: у ОЗОН и получение
 * идентификатора, и набор отказов другие, поэтому его ветка оставлена как была.
 */
export const draftStateInfo = (
  card: Pick<PimMarketplaceCard, 'status' | 'submit_state' | 'external_id'>
    & Partial<Pick<PimMarketplaceCard, 'marketplace' | 'error' | 'last_error_code'>>,
): { label: string; variant: BadgeVariant; hint?: string } => {
  const marketplace = String(card.marketplace ?? '').trim().toLowerCase()
  if (marketplace === 'wb') {
    return wbDraftStateBadge(card, STATUS_INFO)
  }
  const submitState = String(card.submit_state ?? '').trim().toLowerCase()
  const published = String(card.external_id ?? '').trim()
  if (submitState === 'sent' && !published) {
    if (marketplace === 'ym' || marketplace === 'yandex_market') {
      return { label: 'Ждёт номера Маркета', variant: 'yellow', hint: YM_AWAITING_SKU_HINT }
    }
    return { label: 'Ответа нет', variant: 'yellow', hint: UNCONFIRMED_SUBMIT_HINT }
  }
  return STATUS_INFO[card.status] || { label: card.status, variant: 'gray' as const }
}

export const publicationModeLabel = (mode?: string | null) =>
  mode === 'publish' ? 'Публикация' : 'Черновик'

export const formatDraftDateTime = (value?: string | null): string =>
  value
    ? new Date(value).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

const dash = <span className="text-gray-300 dark:text-zinc-600">—</span>

/**
 * В колонке «Товары» показывается наименование, которое уедет на площадку, —
 * то есть SEO-наименование карточки. Внутреннее наименование PIM остаётся
 * запасным вариантом и помечается: без пометки два разных названия в одной
 * колонке неразличимы, и оператор решит, что на МП уедет то, что он видит.
 */
const DraftTitle = ({ card }: { card: PimMarketplaceCard }) => {
  const seoTitle = card.seo_title?.trim()
  if (seoTitle) {
    return (
      <span className="block truncate text-[0.8125rem] font-medium text-gray-900 dark:text-zinc-100" title={seoTitle}>
        {seoTitle}
      </span>
    )
  }
  const pimTitle = card.title?.trim()
  return (
    <span
      className="block truncate text-[0.8125rem] font-medium text-gray-400 dark:text-zinc-500"
      title={pimTitle
        ? `SEO-наименование не создано — показано внутреннее наименование PIM: ${pimTitle}`
        : 'SEO-наименование не создано'}
    >
      {pimTitle || 'Без названия'}
    </span>
  )
}

const Preview = ({ card }: { card: PimMarketplaceCard }) => {
  const sources = draftPreviewSources(card)
  return sources ? (
    <ImagePreviewThumb
      src={sources.src}
      fallbackSrc={sources.fallbackSrc}
      alt={`Визуал карточки ${card.article}`}
      thumbnailWidth={160}
    />
  ) : (
    <ImagePreviewPlaceholder />
  )
}

export const DRAFT_COLUMNS: Array<CatalogColumn<PimMarketplaceCard>> = [
  {
    key: 'product',
    label: 'Товары',
    width: 390,
    minWidth: 300,
    sortColumn: 'article',
    locked: true,
    render: (card, { mode }) => (
      <span className="flex min-w-0 items-center gap-2.5">
        {mode === 'standard' && <Preview card={card} />}
        <span className="min-w-0 flex-1">
          <DraftTitle card={card} />
          <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-400">
            <span className="shrink-0 font-mono text-indigo-700 dark:text-indigo-400">{card.article}</span>
            {card.design_theme && <span className="truncate" title={card.design_theme}>· {card.design_theme}</span>}
            {card.design_legal_status && (
              <span
                className="inline-flex shrink-0"
                role="img"
                aria-label={`Правовой статус: ${normalizeLegalStatus(card.design_legal_status)}`}
                title={`Правовой статус: ${normalizeLegalStatus(card.design_legal_status)}`}
              >
                <CircleAlert className={cn('h-3.5 w-3.5', legalStatusTextClass(card.design_legal_status))} aria-hidden="true" />
              </span>
            )}
          </span>
        </span>
      </span>
    ),
  },
  {
    key: 'design',
    label: 'Макет',
    width: 155,
    minWidth: 120,
    sortColumn: 'design_code',
    render: (card) => card.design_code ? (
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-mono text-xs">#{card.design_code}</span>
        <span className="truncate text-2xs text-gray-500 dark:text-zinc-400">{card.design_type || 'Без типа'}</span>
      </span>
    ) : dash,
  },
  {
    key: 'designCategory',
    label: 'Категория',
    width: 175,
    minWidth: 125,
    render: (card) => card.design_category
      ? <span className="block truncate" title={card.design_category}>{card.design_category}</span>
      : dash,
  },
  {
    key: 'merge',
    label: 'Склейка',
    width: 235,
    minWidth: 165,
    render: (card) => card.merge_label?.trim()
      ? <span className="block truncate" title={card.merge_label}>{card.merge_label}</span>
      : dash,
  },
  {
    key: 'imtId',
    label: 'IMT ID',
    width: 135,
    minWidth: 105,
    render: (card) => card.imt_id != null
      ? <span className="font-mono text-xs">{card.imt_id}</span>
      : dash,
  },
  {
    key: 'printFile',
    label: 'Файл печати',
    width: 125,
    minWidth: 105,
    render: (card) => (
      <Badge variant={card.has_print_file ? 'green' : 'red'}>
        {card.has_print_file ? 'есть' : 'нет'}
      </Badge>
    ),
  },
  {
    key: 'status',
    label: 'Статус',
    width: 155,
    minWidth: 120,
    sortColumn: 'status',
    render: (card) => {
      const info = draftStateInfo(card)
      return (
        <span className="flex flex-wrap items-center gap-1">
          <span title={info.hint}>
            <Badge variant={info.variant}>{info.label}</Badge>
          </span>
          {card.publish_excluded_at && (
            <span title={card.publish_excluded_reason || 'Карточка исключена из выгрузки'}>
              <Badge variant="red">Не выгружать</Badge>
            </span>
          )}
        </span>
      )
    },
  },
  {
    key: 'createdAt',
    label: 'Дата создания',
    width: 150,
    minWidth: 125,
    sortColumn: 'created_at',
    render: (card) => <span className="whitespace-nowrap tabular-nums text-gray-500 dark:text-zinc-400">{formatDraftDateTime(card.created_at)}</span>,
  },
]

export const DRAFT_DEFAULT_VISIBLE = [
  'product', 'design', 'designCategory', 'merge', 'printFile', 'status', 'createdAt',
]

export const DRAFT_XLS_COLUMNS = [
  { key: 'article', header: 'Артикул', value: (card: PimMarketplaceCard) => card.article },
  { key: 'title', header: 'Наименование для МП', value: (card: PimMarketplaceCard) => card.seo_title || '' },
  { key: 'pimTitle', header: 'Наименование PIM', value: (card: PimMarketplaceCard) => card.title || '' },
  { key: 'theme', header: 'Тематика', value: (card: PimMarketplaceCard) => card.design_theme || '' },
  { key: 'legalStatus', header: 'Правовой статус', value: (card: PimMarketplaceCard) => card.design_legal_status || '' },
  { key: 'design', header: 'Код макета', value: (card: PimMarketplaceCard) => card.design_code || '' },
  { key: 'designType', header: 'Тип макета', value: (card: PimMarketplaceCard) => card.design_type || '' },
  { key: 'category', header: 'Категория макета', value: (card: PimMarketplaceCard) => card.design_category || '' },
  { key: 'merge', header: 'Склейка', value: (card: PimMarketplaceCard) => card.merge_label || '' },
  { key: 'imtId', header: 'IMT ID', value: (card: PimMarketplaceCard) => card.imt_id ?? '' },
  { key: 'printFile', header: 'Файл печати', value: (card: PimMarketplaceCard) => card.has_print_file ? 'есть' : 'нет' },
  { key: 'status', header: 'Статус', value: (card: PimMarketplaceCard) => draftStateInfo(card).label },
  { key: 'createdAt', header: 'Дата создания', value: (card: PimMarketplaceCard) => formatDraftDateTime(card.created_at) },
]
